import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import type { DocxMergeFieldValue } from '@/components/templates-v2/document-generation/hooks/mergeFields';
import { TemplatePreview } from '@/components/templates-v2/document-generation/templates/TemplatePreview';
import { Button } from '@/components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { cn } from '@/lib/utils';
import {
  useTemplateFieldsQuery,
  useTemplateRetrieveQuery,
} from '@/services/api/generated/templatesV2Service';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { useGetMatterQuery } from '@/services/api/mattersService';

import {
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_FORMAT_LABELS,
} from './constants';
import {
  type EntityBindings,
  TemplateFieldFiller,
} from './instantiate/TemplateFieldFiller';
import {
  getOverrideKey,
  type MatterContext,
  normalizeTemplateFields,
  validateTemplateFields,
} from './utils/fieldValidation';

const sanitizeFileName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'template';
  }

  const normalized = trimmed
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .slice(0, 80);

  return normalized || 'template';
};

const LoadingState = () => (
  <div className="mx-auto flex h-full min-h-0 w-full max-w-[1880px] flex-col px-4 py-3 md:px-6">
    <div className="h-20 animate-pulse rounded-xl border border-zinc-200 bg-zinc-100/70 dark:border-zinc-800 dark:bg-zinc-900/70" />
    <div className="mt-3 flex min-h-0 flex-1 gap-3">
      <div className="h-full w-[34%] animate-pulse rounded-xl border border-zinc-200 bg-zinc-100/70 dark:border-zinc-800 dark:bg-zinc-900/70" />
      <div className="h-full flex-1 animate-pulse rounded-xl border border-zinc-200 bg-zinc-100/70 dark:border-zinc-800 dark:bg-zinc-900/70" />
    </div>
  </div>
);

class PreviewErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-red-300 bg-red-50/60 px-6 text-center dark:border-red-700 dark:bg-red-900/20">
          <div>
            <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-red-500" />
            <p className="mt-2 text-sm font-semibold text-red-800 dark:text-red-200">
              Preview failed to render
            </p>
            <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
              An unexpected error occurred. Try selecting a different matter or
              refreshing.
            </p>
          </div>
        </div>
      );
    }
    return children;
  }
}

export const TemplateGeneratePage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we came from a firm management context
  const searchParams = new URLSearchParams(location.search);
  const firmContext = searchParams.get('firmContext');

  const templatesListPath = firmContext
    ? `/firms/${firmContext}/templates`
    : location.search
      ? `/templates-v2${location.search}`
      : '/templates-v2';

  const resolvedTemplateId = templateId ?? '';

  const [matterId, setMatterId] = useState('');
  const [entityBindings, setEntityBindings] = useState<EntityBindings>({});
  const [committedOverrides, setCommittedOverrides] = useState<Record<string, string>>({});
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, string>>({});
  const [previewPdfBlob, setPreviewPdfBlob] = useState<Blob | null>(null);

  const {
    data: template,
    isLoading: isTemplateLoading,
    error: templateError,
  } = useTemplateRetrieveQuery(
    { id: resolvedTemplateId },
    { skip: !resolvedTemplateId },
  );

  const { data: fieldsData } = useTemplateFieldsQuery(
    { id: resolvedTemplateId },
    { skip: !resolvedTemplateId },
  );

  const templateFields = useMemo(
    () => normalizeTemplateFields(fieldsData),
    [fieldsData],
  );

  const previewOverrides = useMemo(() => {
    const mapped: Record<string, string> = {};
    templateFields.forEach((field) => {
      const key = getOverrideKey(field);
      if (key in committedOverrides) {
        mapped[key] = committedOverrides[key];
      }
    });
    return mapped;
  }, [committedOverrides, templateFields]);

  const contextParams = useMemo(
    () => ({
      matterId,
      ...entityBindings,
    }),
    [entityBindings, matterId],
  );

  const {
    data: matterContext,
    isFetching: isContextFetching,
    error: contextError,
  } = useGetMatterFlattenedContextQuery(contextParams, { skip: !matterId });

  const { data: selectedMatter } = useGetMatterQuery(matterId, {
    skip: !matterId,
  });

  const isContextReady = !!matterId && !!matterContext && !isContextFetching;

  const resolvedMatterContext = useMemo<MatterContext>(
    () => (matterContext ?? {}) as MatterContext,
    [matterContext],
  );

  const validation = useMemo(
    () => validateTemplateFields(templateFields, resolvedMatterContext, pendingOverrides),
    [templateFields, resolvedMatterContext, pendingOverrides],
  );

  const {isValid} = validation;
  const missingFieldNames = useMemo(
    () => validation.missingFields.map((f) => f.fieldName),
    [validation.missingFields],
  );

  const isDirty = useMemo(() => {
    const allKeys = new Set([
      ...Object.keys(pendingOverrides),
      ...Object.keys(committedOverrides),
    ]);
    return [...allKeys].some(
      (k) => (pendingOverrides[k] ?? '') !== (committedOverrides[k] ?? ''),
    );
  }, [pendingOverrides, committedOverrides]);

  const previewContext = useMemo<
    Record<string, DocxMergeFieldValue | undefined> | undefined
  >(() => {
    if (!matterId || !matterContext) {
      return undefined;
    }

    return {
      __selected_matter_id__: matterId,
      ...matterContext,
    } as Record<string, DocxMergeFieldValue | undefined>;
  }, [matterContext, matterId]);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setPendingOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    const cleaned = Object.entries(pendingOverrides).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (value !== '') acc[key] = value;
        return acc;
      },
      {},
    );
    setCommittedOverrides(cleaned);
    setPendingOverrides(cleaned);
  }, [pendingOverrides]);

  const handleDiscard = useCallback(() => {
    setPendingOverrides(committedOverrides);
  }, [committedOverrides]);

  const handleMatterChange = useCallback((nextMatterId: string) => {
    setMatterId(nextMatterId);
    setEntityBindings({});
    setCommittedOverrides({});
    setPendingOverrides({});
    setPreviewPdfBlob(null);
  }, []);

  const handleResetOverrides = useCallback(() => {
    setCommittedOverrides({});
    setPendingOverrides({});
  }, []);

  const handleDownloadPreview = useCallback(() => {
    if (!previewPdfBlob) {
      toast.error('Preview is not ready yet');
      return;
    }

    const baseName = sanitizeFileName(
      template?.display_name || template?.name || 'template',
    );
    const matterSuffix = matterId ? sanitizeFileName(matterId.slice(0, 8)) : 'preview';
    const url = window.URL.createObjectURL(previewPdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${baseName}_${matterSuffix}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [matterId, previewPdfBlob, template?.display_name, template?.name]);

  if (isTemplateLoading) {
    return <LoadingState />;
  }

  if (!resolvedTemplateId || templateError || !template) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          <h1 className="text-lg font-semibold">Template not found</h1>
          <p className="mt-1 text-sm font-medium">
            We couldn&apos;t load this template. Go back and try again.
          </p>
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(templatesListPath)}
            >
              Back to Templates
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const formatLabel = TEMPLATE_FORMAT_LABELS[template.format] ?? template.format;
  const templateDetailPath = location.search
    ? `/templates-v2/${template.id}${location.search}`
    : `/templates-v2/${template.id}`;
  const isPreviewFormatSupported =
    template.format === 'DOCX' || template.format === 'PDF';
  const overrideCount = Object.keys(committedOverrides).length;
  const missingRequiredCount = missingFieldNames.length;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-zinc-50/40 dark:bg-zinc-950">
      <div className="border-b border-zinc-200/80 bg-white/90 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/85">
        <div className="mx-auto flex w-full max-w-[1880px] min-w-0 flex-col gap-2 px-4 py-3 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2.5">
              <button
                type="button"
                onClick={() => navigate(templateDetailPath)}
                className="rounded-md border border-zinc-200 p-1.5 text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                aria-label="Back to template detail"
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </button>

              <div className="min-w-0">
                <h1 className="truncate text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {template.display_name || template.name}
                </h1>
                <p className="truncate text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                  Generate and validate against live matter context
                </p>
              </div>

              <span
                className={cn(
                  'inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-semibold',
                  TEMPLATE_FORMAT_COLORS[template.format] ??
                    'bg-zinc-100 text-zinc-700',
                )}
              >
                {formatLabel}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => navigate(templateDetailPath)}
              >
                <EyeIcon className="h-4 w-4" />
                Details
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8"
                onClick={handleDownloadPreview}
                disabled={!previewPdfBlob}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(340px,520px)_minmax(0,1fr)_auto]">
            <MatterSelectorCombobox
              value={matterId}
              onChange={handleMatterChange}
              includeAllOption={false}
              placeholder="Select a matter..."
              aria-label="Select matter for template generation"
            />

            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {!matterId && (
                <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  No matter selected
                </span>
              )}

              {matterId && selectedMatter && (
                <span className="inline-flex max-w-full items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
                  <span className="truncate">{selectedMatter.name}</span>
                  <span className="ml-1 shrink-0 text-blue-600/90 dark:text-blue-300/80">
                    ({String(selectedMatter.id).slice(0, 8)})
                  </span>
                </span>
              )}

              {matterId && isValid && (
                <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Required fields ready
                </span>
              )}

              {matterId && !isValid && (
                <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                  {missingRequiredCount} required missing
                </span>
              )}

              {matterId && isContextFetching && (
                <span className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  Refreshing context
                </span>
              )}

              {matterId && overrideCount > 0 && (
                <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-100 px-2 py-1 text-[11px] font-semibold text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {overrideCount} override
                  {overrideCount === 1 ? '' : 's'}
                </span>
              )}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 justify-self-start px-2 text-xs xl:justify-self-end"
              onClick={handleResetOverrides}
              disabled={!matterId}
            >
              Clear overrides
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-full min-h-0 w-full max-w-[1880px] flex-1 px-4 py-3 md:px-6">
        <ResizablePanelGroup
          direction="horizontal"
          className="min-h-0 rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
        >
          <ResizablePanel defaultSize={35} minSize={24} maxSize={50}>
            <aside className="flex h-full min-h-0 flex-col">
              <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-800">
                <div>
                  <h2 className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
                    Field Overrides
                  </h2>
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                    {matterId
                      ? 'Edit values before preview'
                      : 'Select a matter to load fields'}
                  </p>
                </div>
                {matterId && (
                  <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                    {overrideCount} override
                    {overrideCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
                {!matterId ? (
                  <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 px-6 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
                    <div>
                      <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                        Select a matter to start
                      </p>
                      <p className="mt-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                        Fields and bindings will load here.
                      </p>
                    </div>
                  </div>
                ) : (
                  <TemplateFieldFiller
                    templateId={resolvedTemplateId}
                    matterId={matterId}
                    entityBindings={entityBindings}
                    onEntityBindingChange={setEntityBindings}
                    overrides={pendingOverrides}
                    onFieldChange={handleFieldChange}
                    isDirty={isDirty}
                    onApply={handleApply}
                    onDiscard={handleDiscard}
                  />
                )}
              </div>
            </aside>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-zinc-200/80 dark:bg-zinc-800/80" />

          <ResizablePanel defaultSize={65} minSize={50}>
            <main className="flex h-full min-h-0 min-w-0 flex-col">
              <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-800">
                <div>
                  <h2 className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
                    Live Preview
                  </h2>
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                    Merged output with current context and overrides
                  </p>
                </div>
                {!isValid && matterId && (
                  <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                    {missingRequiredCount} required missing
                  </span>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {!matterId && (
                  <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 px-6 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
                    <div>
                      <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                        Select a matter to generate preview
                      </p>
                      <p className="mt-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                        Preview appears immediately after context loads.
                      </p>
                    </div>
                  </div>
                )}

                {matterId && !isPreviewFormatSupported && (
                  <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 px-6 text-center dark:border-zinc-700 dark:bg-zinc-900/50">
                    <div>
                      <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
                        Preview supports DOCX and PDF templates
                      </p>
                      <p className="mt-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                        This template is {formatLabel}. Open details for raw content.
                      </p>
                    </div>
                  </div>
                )}

                {matterId && isPreviewFormatSupported && contextError && (
                  <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-red-300 bg-red-50/60 px-6 text-center dark:border-red-700 dark:bg-red-900/20">
                    <div>
                      <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-red-500" />
                      <p className="mt-2 text-sm font-semibold text-red-800 dark:text-red-200">
                        Failed to load matter context
                      </p>
                      <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
                        Unable to fetch context for this matter. Try selecting a
                        different matter.
                      </p>
                    </div>
                  </div>
                )}

                {matterId &&
                  isPreviewFormatSupported &&
                  !contextError &&
                  !isContextReady && (
                    <div className="flex h-full min-h-[420px] items-center justify-center">
                      <div className="text-center">
                        <ArrowPathIcon className="mx-auto h-6 w-6 animate-spin text-zinc-400" />
                        <p className="mt-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
                          Loading matter context...
                        </p>
                      </div>
                    </div>
                  )}

                {matterId &&
                  isPreviewFormatSupported &&
                  !contextError &&
                  isContextReady && (
                    <PreviewErrorBoundary>
                      <TemplatePreview
                        templateId={resolvedTemplateId}
                        matterId={matterId}
                        matterContext={previewContext}
                        overrides={previewOverrides}
                        onPdfBlobChanged={setPreviewPdfBlob}
                      />
                    </PreviewErrorBoundary>
                  )}
              </div>
            </main>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};
